import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Create (with password) or edit a team member. Managers can only pick Attendant.
export default function TeamMemberDialog({ open, onClose, member, lots, isOwner, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const isNew = !member;

  useEffect(() => {
    if (open) setF(member ? { name: member.name, email: member.email, role: member.role, assignedLotIds: member.assignedLotIds || [] }
      : { name: "", email: "", password: "", role: "attendant", assignedLotIds: [] });
  }, [open, member]);

  const toggleLot = (id) => setF((p) => ({ ...p, assignedLotIds: p.assignedLotIds.includes(id)
    ? p.assignedLotIds.filter((x) => x !== id) : [...p.assignedLotIds, id] }));

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Enter the employee's name");
    if (!/^\S+@\S+\.\S+$/.test(f.email || "")) return toast.error("Enter a valid email address");
    if (isNew && (f.password || "").length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      if (isNew) await api.post("/team", f);
      else await api.patch(`/team/${member.id}`, f);
      toast.success(isNew ? `Account created for ${f.name}` : "Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="team-member-dialog" className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-[480px]">
        <DialogHeader><DialogTitle className="font-heading text-xl">{isNew ? "Add employee" : `Edit ${member.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Full name"><Input data-testid="team-form-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="border-slate-700 bg-slate-950" /></Field>
          <Field label="Email (login)"><Input data-testid="team-form-email" type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="border-slate-700 bg-slate-950" /></Field>
          {isNew && (
            <Field label="Password (min 8 characters)"><Input data-testid="team-form-password" type="text" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} className="border-slate-700 bg-slate-950 font-data" /></Field>
          )}
          <Field label="Role">
            <div className="flex gap-2">
              {["attendant", ...(isOwner ? ["manager"] : [])].map((r) => (
                <Button key={r} type="button" data-testid={`team-form-role-${r}`} onClick={() => setF({ ...f, role: r })}
                  className={`h-9 capitalize ${f.role === r ? "bg-[#A0148C] hover:bg-[#80106F]" : "bg-slate-800 hover:bg-slate-700"}`}>{r}</Button>
              ))}
            </div>
          </Field>
          <Field label={f.role === "manager" ? "Assigned lots (managers see all lots)" : "Assigned lots (attendant sees only these)"}>
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-800 bg-slate-950 p-3">
              {lots.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={`team-form-lot-${l.id}`} checked={(f.assignedLotIds || []).includes(l.id)} onCheckedChange={() => toggleLot(l.id)} />
                  {l.name}{l.isActive === false && <span className="text-xs text-slate-500">(not live)</span>}
                </label>
              ))}
            </div>
          </Field>
          <Button data-testid="team-form-save" onClick={save} disabled={busy} className="h-11 w-full bg-[#A0148C] font-bold hover:bg-[#80106F]">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isNew ? "Create login" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="mb-1 block text-xs text-slate-400">{label}</Label>{children}</div>;
}
